import { getArtists, getArtist, updateArtist, deleteArtist } from '../../api/artists'
import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { CircularProgress, Alert, Snackbar } from '@mui/material'

const Artists = () => {
    const uid = localStorage.getItem('uid')
    const queryClient = useQueryClient()
    
    // Form States
    const [name, setName] = useState('')
    const [website, setWebsite] = useState('')
    const [facebook, setFacebook] = useState('')
    const [twitter, setTwitter] = useState('')
    const [instagram, setInstagram] = useState('')
    const [youtube, setYoutube] = useState('')
    const [spotify, setSpotify] = useState('')
    const [apple, setApple] = useState('')
    const [deezer, setDeezer] = useState('')
    const [soundCloud, setSoundCloud] = useState('')
    const [artistId, setArtistId] = useState('')
    const [currentPage, setCurrentPage] = useState(1)
    
    // Notification State
    const [notification, setNotification] = useState({ open: false, message: '', type: 'success' })

    // Fetch Artists Query
    const { 
        data: artists = [], 
        isLoading,
        error 
    } = useQuery({
        queryKey: ['artists', uid],
        queryFn: () => getArtists(uid),
        select: (data) => data.artists || [],
    })

    // Update Artist Mutation
    const updateArtistMutation = useMutation({
        mutationFn: (data) => updateArtist(uid, data.artistId, data.updatedData),
        onSuccess: () => {
            queryClient.invalidateQueries(['artists', uid])
            setNotification({
                open: true,
                message: 'Artist updated successfully',
                type: 'success'
            })
        },
        onError: (error) => {
            setNotification({
                open: true,
                message: error.message || 'Failed to update artist',
                type: 'error'
            })
        }
    })

    // Delete Artist Mutation
    const deleteArtistMutation = useMutation({
        mutationFn: (index) => deleteArtist(uid, index),
        onSuccess: () => {
            queryClient.invalidateQueries(['artists', uid])
            setNotification({
                open: true,
                message: 'Artist deleted successfully',
                type: 'success'
            })
        },
        onError: (error) => {
            setNotification({
                open: true,
                message: error.message || 'Failed to delete artist',
                type: 'error'
            })
        }
    })

    const updateForm = (artist) => {
        setName(artist.name)
        setWebsite(artist.website)
        setFacebook(artist.facebook)
        setTwitter(artist.twitter)
        setInstagram(artist.instagram)
        setYoutube(artist.youtube)
        setSpotify(artist.spotify)
        setApple(artist.apple)
        setDeezer(artist.deezer)
        setSoundCloud(artist.soundCloud)
        setArtistId(artist.id)
    }

    const editArtist = async () => {
        const updatedData = { 
            name,
            website,
            facebook,
            twitter,
            instagram,
            youtube,
            spotify,
            apple,
            deezer,
            soundcloud: soundCloud
        }
        updateArtistMutation.mutate({ artistId, updatedData })
    }

    const deleteArtistFunc = (index) => {
        deleteArtistMutation.mutate(index)
    }

    /////////// PAGINATION ///////////
    const itemsPerPage = 25
    const indexOfLastItem = currentPage * itemsPerPage
    const indexOfFirstItem = indexOfLastItem - itemsPerPage
    const currentItems = artists.slice(indexOfFirstItem, indexOfLastItem)
    const totalPages = Math.ceil(artists.length / itemsPerPage)

    return (
        <div className='dash-section'>
            <h1 className='dash-title'>Artists</h1>
            <div className="row">
                <div className="col-12 mt-4">
                    <div className="dash-card">
                        <div className="dash-card-heading">
                            <h2 className="dash-card-title">Artists</h2>
                            <Link to={'create'} className="dash-card-heading-btn">
                                <i className="fi fi-sr-plus"></i>
                                Add Artist
                            </Link>
                        </div>

                        {/* Loading State */}
                        

                        {/* Error State */}
                        {error && (
                            <Alert severity="error" sx={{ margin: '20px 0' }}>
                                {error.message || 'Failed to load artists'}
                            </Alert>
                        )}
                       

                        {/* Empty State */}
                        {!isLoading && !error && artists.length === 0 && (
                            <div className="text-center py-5">
                                <h5>No artists found</h5>
                                <p>Start by adding your first artist</p>
                            </div>
                        )}

                        {/* Data Table */}
                        { (
                            <div className="table-responsive mt-3">
                                <table className="table dash-table">
                                    <thead>
                                        <tr>
                                            <td>Name</td>
                                            <td>Artist Image</td>
                                            <td>Spotify URL</td>
                                            <td>Action</td>
                                        </tr>
                                    </thead>
                                    <tbody>
                                    {isLoading && (
                                      <tr>
                                        <td colSpan="4" className="text-center">
                                          <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                          </div>
                                        </td>
                                      </tr>
                                    )}
                                        {!isLoading &&currentItems?.map((artist, i) => (
                                            <tr key={i}>
                                                <td>
                                                    <div className="table-item">
                                                        {artist.name}
                                                    </div>
                                                </td>
                                                <td>
                                                    <img className='label-logo' src="https://logopond.com/logos/7a55e3234f9e9db641070988915f9b00.png" alt="" />
                                                </td>
                                                <td>
                                                    <div className="table-item">
                                                        <a href={artist.spotify} target='blank'>{artist.spotify}</a>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="table-item d-flex">
                                                        <i 
                                                            onClick={() => updateForm(artist)} 
                                                            className="fi fi-rr-pencil table-action-btn a-blue"
                                                            data-bs-toggle="modal" 
                                                            data-bs-target="#editArtist"
                                                        ></i>
                                                        <i 
                                                            onClick={() => deleteArtistFunc(artist.id)}
                                                            className="fi fi-rr-trash table-action-btn a-red ms-2"
                                                            disabled={deleteArtistMutation.isLoading}
                                                        ></i>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Pagination */}
                        {!isLoading && !error && artists.length > 0 && (
                            <div className="table-pagination">
                                {Array.from({ length: totalPages }, (_, index) => index + 1).map((pageNumber) => (
                                    <button 
                                        key={pageNumber} 
                                        className={currentPage === pageNumber ? 'active' : ''} 
                                        onClick={() => setCurrentPage(pageNumber)}
                                    >
                                        {pageNumber}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Edit Modal */}
            <div className="modal fade" id="editArtist" tabIndex="-1" aria-labelledby="editArtistLabel" aria-hidden="true">
                <div className="modal-dialog modal-lg modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h1 className="modal-title fs-5" id="editArtistLabel">Edit Artist</h1>
                            <button type="button" className="btn-close"  data-bs-dismiss="modal" aria-label="Close" data-bs-target="#editTrackModal"></button>
                        </div>
                        <div className="modal-body">
                            <div className="row">
                                <div className="col-12">
                                    <h4 className='dash-form-heading'>General Information</h4>
                                </div>
                                <div className="col-md-6 mt-3">
                                    <div className="dash-form-group">
                                        <label>Name</label>
                                        <input value={name} onChange={(e) => setName(e.target.value)} type="text" placeholder='Name' className='dash-form-input' />
                                    </div>
                                </div>
                                <div className="col-md-6 mt-3">
                                    <div className="dash-form-group">
                                        <label>Website</label>
                                        <input value={website} onChange={(e) => setWebsite(e.target.value)} type="text" placeholder='Website' className='dash-form-input' />
                                    </div>
                                </div>
                                <div className="col-12 mt-4">
                                    <h4 className='dash-form-heading'>Profile Information</h4>
                                </div>
                                <div className="col-md-6 mt-3">
                                    <div className="dash-form-group">
                                        <label>Facebook</label>
                                        <input value={facebook} onChange={(e) => setFacebook(e.target.value)} type="text" placeholder='Facebook' className='dash-form-input' />
                                    </div>
                                </div>
                                <div className="col-md-6 mt-3">
                                    <div className="dash-form-group">
                                        <label>Twitter</label>
                                        <input value={twitter} onChange={(e) => setTwitter(e.target.value)} type="text" placeholder='Twitter' className='dash-form-input' />
                                    </div>
                                </div>
                                <div className="col-md-6 mt-3">
                                    <div className="dash-form-group">
                                        <label>Instagram</label>
                                        <input value={instagram} onChange={(e) => setInstagram(e.target.value)} type="text" placeholder='Instagram' className='dash-form-input' />
                                    </div>
                                </div>
                                <div className="col-md-6 mt-3">
                                    <div className="dash-form-group">
                                        <label>Youtube</label>
                                        <input value={youtube} onChange={(e) => setYoutube(e.target.value)} type="text" placeholder='Youtube' className='dash-form-input' />
                                    </div>
                                </div>
                                <div className="col-md-6 mt-3">
                                    <div className="dash-form-group">
                                        <label>Spotify</label>
                                        <input value={spotify} onChange={(e) => setSpotify(e.target.value)} type="text" placeholder='Spotify' className='dash-form-input' />
                                    </div>
                                </div>
                                <div className="col-md-6 mt-3">
                                    <div className="dash-form-group">
                                        <label>Apple</label>
                                        <input value={apple} onChange={(e) => setApple(e.target.value)} type="text" placeholder='Apple' className='dash-form-input' />
                                    </div>
                                </div>
                                <div className="col-md-6 mt-3">
                                    <div className="dash-form-group">
                                        <label>Deezer</label>
                                        <input value={deezer} onChange={(e) => setDeezer(e.target.value)} type="text" placeholder='Deezer' className='dash-form-input' />
                                    </div>
                                </div>
                                <div className="col-md-6 mt-3">
                                    <div className="dash-form-group">
                                        <label>SoundCloud</label>
                                        <input value={soundCloud} onChange={(e) => setSoundCloud(e.target.value)} type="text" placeholder='SoundCloud' className='dash-form-input' />
                                    </div>
                                </div>
                                <div className="col-12 mt-3">
                                    <div className="dash-form-btns">
                                        <button 
                                            className='dash-form-btn' 
                                            data-bs-dismiss="modal" 
                                            data-bs-target="#editTrackModal"
                                            onClick={editArtist}
                                            disabled={updateArtistMutation.isLoading}
                                        >
                                            {updateArtistMutation.isLoading ? 'Updating...' : 'Submit'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Notification Snackbar */}
            <Snackbar
                open={notification.open}
                autoHideDuration={6000}
                onClose={() => setNotification({ ...notification, open: false })}
            >
                <Alert 
                    onClose={() => setNotification({ ...notification, open: false })} 
                    severity={notification.type}
                    sx={{ width: '100%' }}
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </div>
    )
}

export default Artists